import { FC, useEffect, useMemo, useRef } from 'react';
import { observer } from 'mobx-react';
import { useQueryParams } from 'hooks/useQueryParams';
import SuggestionSearch from './SuggestionSearch/SuggestionSearch';
import Subscription from './Subscription/Subscription';
import TreeViewDropdown from './TreeViewDropdown/TreeViewDropdown';
import Checkbox from 'components/Checkbox';
import NavigationList from 'components/NavigationList';
import { ReactComponent as Location } from 'assets/img/location.svg';
import { DROPDOWN_TYPE, lotTypes, productTypes, SearchParams } from 'constants/common';
import { createCategoriesDropdownOptions, createLocationsDropdownOptions } from 'helpers/utils';
import { useStores } from 'stores';
import styles from './Filters.module.scss';
import { FormGroup } from 'uiKit/atoms/Group/FormGroup/FormGroup';

interface FilterProps {
  name?: string;
  value?: string | number | null;
}

interface Props {
  onFilterChange: (arg0: FilterProps[]) => FilterProps;
  hideFilters: boolean;
}

const Filters: FC<Props> = ({onFilterChange, hideFilters}) => {
  const { userStore, singleLotStore, lotStore } = useStores();
  const { queryParams } = useQueryParams();
  const { lotCategories: categories, getCategories } = singleLotStore;
  const { locations, getLocations } = userStore;
  const { filters } = lotStore as any;
  const searchLotType: any = useMemo(() => queryParams.lotType || 'all', [queryParams]);

  const filtersPanel = useRef<HTMLDivElement | null>(null);

  const categoryListOptions = createCategoriesDropdownOptions(categories);
  const locationsListOptions = createLocationsDropdownOptions(locations);

  const setLocationValueHandler = (newValue: string) => {
    onFilterChange([{ name: 'locationIds', value: newValue }]);
  };

  const setCategoryValueHandler = (newValue: string) => {
    onFilterChange([{ name: 'categoryId', value: newValue }]);
  };

  const setProductTypeValueHandler = (newValue: string) => {
    if (newValue === 'new' || newValue === 'used') {
      onFilterChange([
        { name: 'productType', value: 'goods' },
        { name: 'lotState', value: newValue },
      ]);
    } else {
      onFilterChange([
        { name: 'productType', value: newValue },
        { name: 'lotState', value: null },
      ]);
    }
  };

  const setLotTypeHandler = (newValue: string) => {
    onFilterChange([{ name: 'lotType', value: newValue }]);
  };

  const setTag = (newValue: string) => {
    onFilterChange([{ name: 'tagIds', value: newValue }]);
  };

  const setDefaultPageNumber = () => {
    onFilterChange([{ name: 'pageNumber', value: 1 }]);
  };

  useEffect(() => {
    const el = filtersPanel.current;
    if(el) {
      el.style.top = el.offsetTop + 'px';
    }
  }, [filtersPanel, hideFilters]);

  useEffect(() => {
    getLocations();
    getCategories();
  }, [queryParams]);

  const productTypeFilterValue =
    filters.lotState === 'used' || filters.lotState === 'new'
      ? [filters.productType, filters.lotState]
      : filters.productType;

  return hideFilters ? null : (
    <div className={styles.bns3_filtersWrapper} ref={filtersPanel}>
      <div className={styles.bns3_filters}>
        <div>
          <div className={styles.bns3_filters_locationDropdown}>
            <TreeViewDropdown
              placeholder="Location"
              options={locationsListOptions}
              value={filters.locationIds}
              setValue={setLocationValueHandler}
              multiple
              icon={<Location />}
            />
          </div>
        </div>

        <div className={styles.bns3_search_group_container}>
          <NavigationList data={lotTypes} activeItem={searchLotType?.toLowerCase()} onClick={setLotTypeHandler} />
        </div>

        <div className={styles.bns3_filters_container}>
          <TreeViewDropdown
            placeholder="Category"
            options={categoryListOptions}
            value={filters.categoryId}
            setValue={setCategoryValueHandler}
            multiple={false}
          />
          <div className={styles.bns3_filters_suggestionSearch}>
            <SuggestionSearch setTag={setTag} onFilterChange={onFilterChange} setDefaultPageNumber={setDefaultPageNumber}/>
          </div>
          <TreeViewDropdown
            options={productTypes}
            placeholder="Product Type"
            value={productTypeFilterValue}
            setValue={setProductTypeValueHandler}
            multiple={false}
            valueStoreType={DROPDOWN_TYPE.VALUE_STORE.RELATIVE}
          />
          {/* <Button className={styles.bns3_filters_container} onClick={handleSearch}>
            Search
          </Button> */}
        </div>

        <div>
          <div className={styles.bns3_search_checkbox_container}>
            <FormGroup row>
              {SearchParams.map(el => (
              <Checkbox
                id={el.name}
                key={el.name}
                value={el.name}
                isChecked={filters[el.name]}
                label={el.value}
                onChange={onFilterChange}
              />
            ))}
            </FormGroup>
          </div>
          <Subscription queryParams={queryParams} />
        </div>
      </div>
    </div>
  );
};

export default observer(Filters);
